import React from 'react'
import { useTheme, Tooltip, Checkbox } from '@material-ui/core'
import { Issue, Lease, Unit } from '../../../../models'
import { Attachment, Container } from '../../../../components'
import { ReactComponent as ActionItemIcon } from '../../../../assets/icons/action-item.svg'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface IssueData {
    issue: Issue
    lease: Lease
    areaName: string
    unitName: string
    tenantName: string
    tenantEmail: string
    startDate: string
    endDate: string
    inventoryConfigName: string
}

interface IssueRowProps {
    issueData: IssueData
    unitMap: Record<number, Unit>
    selectionMode?: boolean
    isSelected?: boolean
    onSelectionChange?: () => void
    onClick?: (issueData: IssueData) => void
}

export const IssueRow: React.FC<IssueRowProps> = ({
    issueData,
    unitMap,
    selectionMode = false,
    isSelected = false,
    onSelectionChange,
    onClick,
}) => {
    const {
        issue,
        lease,
        areaName,
        unitName,
        tenantName,
        tenantEmail,
        startDate,
        endDate,
        inventoryConfigName,
    } = issueData
    const theme = useTheme()

    const rowWidthCalc = `calc(100vw - 300px - 250px - 20px - 20px - 135px)`

    // Get folder info from the unit
    const unit = unitMap[lease.area.unit]
    const location = `${unit?.folder?.path ?? ''}${unit?.folder?.name ?? ''}`

    // Check if issue is disabled (has workorder in selection mode)
    const isDisabled = selectionMode && issue.workorder_id

    // Handle row click
    const handleClick = () => {
        if (selectionMode && !issue.workorder_id) {
            onSelectionChange?.()
        } else {
            onClick?.(issueData)
        }
    }

    // Handle checkbox click
    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!issue.workorder_id) {
            onSelectionChange?.()
        }
    }

    // Handle mouse events for hover effects
    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        if (onClick || (selectionMode && !issue.workorder_id)) {
            e.currentTarget.style.transform = 'translateY(-1px)'
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'
        }
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        if (onClick || (selectionMode && !issue.workorder_id)) {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = 'none'
        }
    }

    return (
        <div
            style={{
                backgroundColor:
                    selectionMode && isSelected
                        ? theme.palette.grey[300]
                        : 'white',
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.grey[300]}`,
                minHeight: 200,
                maxHeight: 200,
                cursor:
                    selectionMode && !issue.workorder_id
                        ? 'pointer'
                        : onClick
                        ? 'pointer'
                        : 'default',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                display: 'flex',
                position: 'relative',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'grayscale(0.3)' : 'none',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            {/* Selection Checkbox */}
            {selectionMode && (
                <Container
                    style={{
                        position: 'absolute',
                        top: theme.spacing(1),
                        left: theme.spacing(1),
                        zIndex: 2,
                        width: 24,
                        height: 24,
                    }}
                    onClick={handleCheckboxClick}
                >
                    <Checkbox
                        checked={isSelected}
                        disabled={!!issue.workorder_id}
                        style={{
                            color: theme.palette.primary.main,
                            padding: 0,
                            width: 24,
                            height: 24,
                        }}
                    />
                </Container>
            )}

            {/* Disabled Overlay for Issues with Workorders */}
            {isDisabled && (
                <Container
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: theme.shape.borderRadius,
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            )}

            {/* Left Column - Location, Tenant, Inventory Item, Lease Dates, Workorder Icon */}
            <Container
                direction="column"
                style={{
                    padding: theme.spacing(1, 2, 1, selectionMode ? 4 : 2),
                    fontWeight: 700,
                    ...typographyStyle(theme.typography.body1),
                    minWidth: 300,
                    maxWidth: 300,
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                }}
            >
                {location && (
                    <span
                        style={{
                            fontWeight: 400,
                            color: isDisabled
                                ? theme.palette.grey[500]
                                : theme.palette.text.primary,
                        }}
                    >
                        {location}
                    </span>
                )}

                <span
                    style={{
                        fontWeight: 700,
                        color: isDisabled
                            ? theme.palette.grey[500]
                            : theme.palette.text.primary,
                    }}
                >
                    {unitName} - {areaName} ({lease.area.area_label})
                </span>

                <div style={{ flex: 1 }} />

                <Container
                    direction="column"
                    style={{ alignItems: 'flex-start' }}
                >
                    <span
                        style={{
                            fontWeight: 400,
                            fontSize: '12px',
                            color: isDisabled
                                ? theme.palette.grey[400]
                                : theme.palette.grey[600],
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        {tenantName}
                    </span>

                    <span
                        style={{
                            fontWeight: 400,
                            color: isDisabled
                                ? theme.palette.grey[400]
                                : theme.palette.grey[600],
                            fontSize: '12px',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        {tenantEmail}
                    </span>

                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing(1),
                            width: '100%',
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 400,
                                color: isDisabled
                                    ? theme.palette.grey[400]
                                    : theme.palette.grey[600],
                            }}
                        >
                            {startDate} - {endDate}
                        </span>

                        {/* Workorder Icon */}
                        {issue.workorder_id && (
                            <Tooltip title="Workorder Created">
                                <ActionItemIcon
                                    height={25}
                                    width={25}
                                    style={{
                                        color: theme.palette.success.main,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Container>
                </Container>
            </Container>

            {/* Middle Column - Inventory Item and Issue Description */}
            <Container
                style={{
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                    flex: 1,
                    padding: theme.spacing(2),
                    minWidth: 250,
                    maxWidth: 250,
                    overflow: 'auto',
                }}
                direction="column"
            >
                <span
                    style={{
                        fontWeight: 700,
                        color: isDisabled
                            ? theme.palette.grey[500]
                            : theme.palette.grey[700],
                        fontSize: '14px',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    {inventoryConfigName}
                </span>

                <span
                    style={{
                        fontWeight: 700,
                        ...typographyStyle(theme.typography.subtitle1),
                        color: isDisabled
                            ? theme.palette.grey[500]
                            : theme.palette.text.primary,
                        marginBottom: theme.spacing(1),
                    }}
                >
                    {issue.description || 'No description available'}
                </span>
            </Container>

            {/* Right Column - Attachments */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    paddingBottom: 0,
                    paddingRight: 0,
                    alignItems: 'center',
                    overflowX: 'auto',
                    maxWidth: rowWidthCalc,
                }}
            >
                {issue.message?.attachments &&
                issue.message.attachments.length > 0 ? (
                    issue.message.attachments.map((attachment) => (
                        <div key={`ATTACHMENT_${attachment.id}`}>
                            <Attachment
                                attachment={attachment.file}
                                uploaded={attachment.uploaded}
                                theme={theme}
                                size={175}
                            />
                        </div>
                    ))
                ) : (
                    <span
                        style={{
                            fontSize: '14px',
                            color: isDisabled
                                ? theme.palette.grey[300]
                                : theme.palette.grey[400],
                            fontStyle: 'italic',
                            padding: theme.spacing(2),
                        }}
                    >
                        No attachments
                    </span>
                )}
            </Container>
        </div>
    )
}
